import {format} from 'date-fns';
import {Link} from 'react-router-dom';
import {NewsPage} from "@/hooks/useNews.ts";
import TablePagination from "@/components/TablePagination.tsx";
import LoadingGridItems from "@/components/LoadingGridItems.tsx";

type Props = {
    content: NewsPage;
    isLoading?: boolean;
    isError?: boolean;
    seeMoreDisplay?: boolean;
    seeMorePath?: string;
    seeMoreLabel?: string;
    showCategory?: boolean;
    showTags?: boolean;
    showPagination?: boolean;
    setPage: (p: number) => void;
};

export default function NewsGrid({
                                     content,
                                     isLoading,
                                     isError,
                                     seeMoreDisplay = true,
                                     seeMorePath = '/nuus',
                                     seeMoreLabel = 'Ontdek meer',
                                     showCategory = true,
                                     showTags = true,
                                     showPagination = true,
                                     setPage,
                                 }: Props) {

    const items = content?.items ?? [];

    return (
        <>
            <div className="mx-auto">

                {isLoading && <LoadingGridItems/>}

                {isError && <p className="mt-6 text-sm text-red-600 text-center">Failed to load News.</p>}

                {items.length === 0 && !isLoading && !isError && (
                    <p className="mt-6 text-sm text-gray-600 text-center">No News found.</p>
                )}

                <div
                    className="grid max-w-2xl grid-cols-1 gap-x-8 gap-y-10 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                    {items.map((post) => (
                        <article key={post.id} className="flex flex-col items-start justify-between">

                            {/* Media */}
                            {post.media?.url && (
                                <Link to={`/nuus/${post.slug}`} className="relative">
                                    <div className="relative w-full">
                                        <img
                                            alt={post.title}
                                            src={post.media.url}
                                            className="w-full rounded-2xl bg-gray-100 object-cover aspect-video sm:aspect-[2/1] lg:aspect-[3/2]"
                                        />
                                        <div className="absolute inset-0 rounded-2xl ring-1 ring-gray-900/10"/>
                                    </div>
                                </Link>
                            )}

                            {/* Meta + Content */}
                            <div className="flex max-w-xl grow flex-col justify-between">
                                <div className="mt-4 flex items-center gap-x-4 text-xs">
                                    <time dateTime={post.publishedAt} className="text-gray-600">
                                        {format(new Date(post.publishedAt), 'MMM d, yyyy HH:mm')}
                                    </time>

                                    {showCategory && post.category && (
                                        <Link
                                            to={`/nuus/kategorieë?name=${post.category.slug}`}
                                            className="relative z-10 rounded-full bg-gray-50 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100"
                                        >
                                            {post.category.name}
                                        </Link>
                                    )}
                                </div>

                                <div className="group relative grow">
                                    <h3 className="mt-4 text-lg/6 font-semibold text-blue-900 group-hover:text-gray-600">
                                        <Link to={`/nuus/${post.slug}`} className="relative">
                                            <span className="absolute inset-0" aria-hidden="true"/>
                                            {post.title}
                                        </Link>
                                    </h3>
                                    <p className="mt-4 line-clamp-3 text-md text-gray-600">
                                        {post.summary ?? post.body}
                                    </p>
                                </div>

                                <div className="flex items-center gap-x-4 text-xs">
                                    {showTags && post.tags && post.tags.length > 0 && (
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {post.tags.map((tag) => (
                                                <span
                                                    key={tag.id}
                                                    className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600"
                                                >
                                                {tag.name}
                                            </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                {/* See more */}
                {seeMoreDisplay && (
                    <div className="mt-12 flex justify-center">
                        <Link
                            to={seeMorePath}
                            className="inline-flex items-center rounded-md bg-blue-900 px-3 py-2 text-md font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
                        >
                            {seeMoreLabel}
                        </Link>
                    </div>
                )}
            </div>

            {showPagination && !isLoading && !isError && (
                <TablePagination
                    className="mt-10 flex justify-center"
                    page={content.page}
                    size={content.size}
                    total={content.total}
                    onPage={setPage}
                />
            )}
        </>
    );
}
